import ReactGA from 'react-ga4';

export const initializeGA4 = () => {
  console.log('Initializing GA4 with ID:', process.env.REACT_APP_GA4_ID);
  ReactGA.initialize(process.env.REACT_APP_GA4_ID);
};

export const trackHomePage = () => {
  console.log('Tracking Home Page View:', '/builder/');
  ReactGA.send({ hitType: 'pageview', page: '/builder/' });
};

export const trackProductSelection = (productName: string): void => {
  console.log(`Tracking Product Selection: ${productName}`);
  ReactGA.event({
    category: 'Builder',
    action: `Selected product: ${productName}`,
    label: `User selected ${productName}`,
  });
};

export const trackStage = (stageName: string): void => {
  console.log(`Tracking Stage Progress: ${stageName}`);
  ReactGA.event({
    category: 'Builder',
    action: `Progress to ${stageName}`,
    label: `User is on ${stageName} stage`,
  });
};

export const trackSubStage = (mainStage: string, subStage: string): void => {
  console.log(`Tracking Sub-Stage Progress: ${mainStage} - ${subStage}`);
  ReactGA.event({
    category: 'Builder',
    action: `Progress to ${mainStage} - ${subStage}`,
    label: `User is on ${subStage} under ${mainStage}`,
  });
};

export const trackImageUpload = (side: string): void => {
  if (side === 'left') {
    console.log(`Tracking Image Upload: image uploaded on left`);
    ReactGA.event({
      category: 'Builder',
      action: 'Image uploaded on left',
      label: `User uploaded image on left`,
    });
  } else if (side === 'right') {
    console.log(`Tracking Image Upload: image uploaded on right`);
    ReactGA.event({
      category: 'Builder',
      action: 'Image uploaded on right',
      label: `User uploaded image on right`,
    });
  } else if (side === 'front') {
    console.log(`Tracking Image Upload: image uploaded on top`);
    ReactGA.event({
      category: 'Builder',
      action: 'Image uploaded on top',
      label: `User uploaded image on top`,
    });
  } else if (side === 'back') {
    console.log(`Tracking Image Upload: image uploaded on bottom`);
    ReactGA.event({
      category: 'Builder',
      action: 'Image uploaded on bottom',
      label: `User uploaded image on bottom`,
    });
  } else {
    console.error(`Invalid side: ${side}`);
  }
};

export const trackRemovedUploadedImage = (side: string): void => {
  if (side === 'left') {
    console.log(`Tracking Image Remove: image removed on left`);
    ReactGA.event({
      category: 'Builder',
      action: 'Image removed on left',
      label: `User removed image on left`,
    });
  } else if (side === 'right') {
    console.log(`Tracking Image Remove: image removed on right`);
    ReactGA.event({
      category: 'Builder',
      action: 'Image removed on right',
      label: `User removed image on right`,
    });
  } else if (side === 'front') {
    console.log(`Tracking Image Remove: image removed on top`);
    ReactGA.event({
      category: 'Builder',
      action: 'Image removed on top',
      label: `User removed image on top`,
    });
  } else if (side === 'back') {
    console.log(`Tracking Image Remove: image removed on bottom`);
    ReactGA.event({
      category: 'Builder',
      action: 'Image removed on bottom',
      label: `User removed image on bottom`,
    });
  } else {
    console.error(`Invalid side: ${side}`);
  }
};

export const trackAddGamer = () => {
  console.log('Tracking Add New Gamer Action');
  ReactGA.event({
    category: 'Builder',
    action: 'Add new gamer',
    label: 'User clicked to add new gamer',
  });
};

export const trackRemoveGamer = (index: number): void => {
  console.log(`Tracking Remove Gamer ${index} Action`);
  ReactGA.event({
    category: 'Builder',
    action: `Remove gamer ${index}`,
    label: `User clicked to remove gamer ${index}`,
  });
};

export const trackSaveState = () => {
  console.log('Tracking Save Current State Action');
  ReactGA.event({
    category: 'Builder',
    action: 'Save current state',
    label: 'User saved current state of builder',
  });
};

export const trackShareBuild = (method: string): void => {
  console.log(`Tracking Share Build Action: sharing by ${method}`);
  ReactGA.event({
    category: 'Builder',
    action: `Share build by ${method}`,
    label: `User shared the build by ${method}`,
  });
};

export const trackAddToCart = (
  gender: string,
  size: string,
  productType: string,
  sleeve: string,
  quantity: number,
  pattern: string
): void => {
  console.log(`Tracking Add Item to Cart Action: Gender: ${gender}, Size: ${size}, Product Type: ${productType}, Sleeve: ${sleeve}, Quantity: ${quantity}, Pattern: ${pattern}`);
  
  ReactGA.event({
    category: 'Builder',
    action: 'Add item to cart',
    label: `Gender: ${gender}, Size: ${size}, Product Type: ${productType}, Sleeve: ${sleeve}, Quantity: ${quantity}, Pattern: ${pattern}`,
  });
};

export const trackGoToCart = (method: string): void => {
  console.log(`Tracking Go To Cart Action via: ${method}`);
  ReactGA.event({
    category: 'Builder',
    action: `Go to cart via ${method}`,
    label: `User clicked ${method} to go to cart`,
  });
};

export const trackGoToAccount = () => {
  console.log(`Tracking Go To Account Action`);
  ReactGA.event({
    category: 'Builder',
    action: `Go to account`,
    label: `User go to account`,
  });
};

export const trackReturnToStart = (stage: string): void => {
  console.log(`Tracking User Return to Start Page from ${stage}`);
  ReactGA.event({
    category: 'Builder',
    action: `User returned to start page from ${stage}`,
    label: `User navigated back to the start page from ${stage}`,
  });
};

export const trackBeforeUnload = (stage: string): void => {
  window.addEventListener('beforeunload', () => {
    console.log(`Tracking Before Unload for Stage: ${stage}`);
    ReactGA.event({
      category: 'Builder',
      action: `User exited from ${stage}`,
      label: 'User closed tab or navigated away',
    });
  });
};

export const trackAdditionalFeatures = (feature, value) => {
  console.log(`Tracking Additional Feature Selected: ${feature} with value: ${value}`);
  ReactGA.event({
    category: 'Builder',
    action: `Selected ${feature}`,
    label: `User selected ${value} for ${feature}`,
  });
};

export function trackTimeSpentOnBuilder(startTime: number): void {
  const timeSpentMs = Date.now() - startTime;
  const timeSpentMinutes = timeSpentMs / 1000 / 60;


  const timeSpentLabel = timeSpentMinutes < 1
    ? 'less than 1 minute'
    : `${Math.floor(timeSpentMinutes)} minutes`;

  console.log(`Tracking Time Spent on Builder: ${timeSpentLabel}`);

  ReactGA.event({
    category: 'Builder',
    action: 'Time spent on Builder',
    label: `User spent ${timeSpentLabel} on Builder`,
  });
}

export function trackTimeSpentOnStage(stage: string, stageStartTime: number): void  {
  const timeSpentMs = Date.now() - stageStartTime;
  const timeSpentMinutes = timeSpentMs / 1000 / 60;


  const timeSpentLabel = timeSpentMinutes < 1
    ? 'less than 1 minute'
    : `${Math.floor(timeSpentMinutes)} minutes`;

  console.log(`Tracking Time Spent on Stage: ${stage} - ${timeSpentLabel}`);
  
  ReactGA.event({
    category: 'Builder',
    action: `Time spent on ${stage}`,
    label: `User spent ${timeSpentLabel} on ${stage}`,
  });
}
